import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coins, PlusCircle, CreditCard, Check } from 'lucide-react';
import * as api from '../../api/client';
import { AppError } from '../../api/request';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import PageHeader from '../components/PageHeader';

const STATUS_KEY: Record<string, string> = {
  active: 'billing.status.active',
  cancelled: 'billing.status.cancelled',
  expired: 'billing.status.expired',
  '': 'billing.status.none',
};

export default function BillingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState<api.Plan[]>([]);
  const [creditPack, setCreditPack] = useState<api.CreditPack>({ credits: 100, priceUsd: 9 });
  const [history, setHistory] = useState<api.CreditTransaction[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listPlansCatalog().then((c) => {
      setPlans(c.plans);
      setCreditPack(c.creditPack);
    }).catch(() => {});
    api.getCreditsHistory().then(setHistory).catch(() => {});
  }, []);

  const currentPlan = plans.find((p) => p.id === user?.planId);
  const hasSubscription = !!user?.hasSubscription;
  const hasPlan = !!user?.planId;
  // DEV_SKIP_BILLING can set subStatus=active without a real Lemon sub — don't show Active/Cancel.
  const displayStatus = hasSubscription || hasPlan ? (user?.subStatus ?? '') : '';

  async function upgrade(planId: string) {
    setError('');
    setBusy(true);
    try {
      const url = await api.createCheckout(planId);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('billing.errors.generic'));
      setBusy(false);
    }
  }

  async function buyCredits() {
    setError('');
    setBusy(true);
    try {
      const url = await api.createCreditsCheckout();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('billing.errors.creditsPurchase'));
      setBusy(false);
    }
  }

  async function cancel() {
    const ok = await confirm({
      title: t('billing.cancelSub'),
      message: t('billing.cancelConfirm'),
      confirmLabel: t('billing.cancelSub'),
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.cancelSubscription();
      await refreshUser();
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('billing.errors.cancelFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    setBusy(true);
    try {
      await api.resumeSubscription();
      await refreshUser();
    } catch (err) {
      setError(err instanceof AppError ? err.message : t('billing.errors.resumeFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader icon={CreditCard} title={t('billing.title')} />
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="relative overflow-hidden card p-5 flex items-center justify-between flex-wrap gap-4">
          <div aria-hidden className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-brand-purple/10 blur-2xl" />
          <div className="relative">
            <p className="text-sm text-gray-500 dark:text-lc-muted">{t('billing.currentPlan')}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-lc-text">{currentPlan?.name || user?.planId || '—'}</p>
            <p className="text-sm text-gray-400 dark:text-lc-faint mt-1">{t(STATUS_KEY[displayStatus] ?? 'billing.status.none')}</p>
          </div>
          <div className="relative flex items-center gap-2">
            {!hasSubscription && !hasPlan && (
              <button onClick={() => navigate('/select-plan')} disabled={busy} className="btn-primary">{t('billing.subscribe')}</button>
            )}
            {hasSubscription && user?.subStatus === 'active' && (
              <button onClick={cancel} disabled={busy} className="btn-secondary">{t('billing.cancelSub')}</button>
            )}
            {hasSubscription && user?.subStatus === 'cancelled' && (
              <button onClick={resume} disabled={busy} className="btn-primary">{t('billing.resumeSub')}</button>
            )}
          </div>
        </div>

        <div className="card p-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span className="icon-chip">
              <Coins className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm text-gray-500 dark:text-lc-muted">{t('billing.creditBalance')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-lc-text">{user?.aiCredits.balance ?? 0}</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-lc-faint">
                {t('billing.creditPackMeta', { credits: creditPack.credits, price: creditPack.priceUsd })}
              </p>
            </div>
          </div>
          <button onClick={buyCredits} disabled={busy} className="btn-gradient">
            <PlusCircle className="h-4 w-4" /> {t('billing.buyCredits')}
          </button>
        </div>
      </div>

      <h2 className="font-semibold text-gray-900 dark:text-lc-text mb-3">{t('billing.plansTitle')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {plans.map((p) => {
          const isCurrent = p.id === user?.planId;
          return (
            <div key={p.id} className={`card p-5 transition-shadow ${isCurrent ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-lc-text">{p.name}</h3>
                {isCurrent && (
                  <span className="badge bg-primary-50 text-primary-700 gap-1">
                    <Check className="h-3 w-3" /> {t('billing.currentPlan')}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xl font-extrabold text-gray-900 dark:text-lc-text">${p.monthlyPriceUsd}<span className="text-sm font-normal text-gray-400 dark:text-lc-faint">{t('home.perMonth')}</span></p>
              <p className="mt-2 text-xs text-gray-500 dark:text-lc-muted">{t('billing.accountsAndCredits', { accounts: p.maxInstagramAccounts, credits: p.monthlyAiCredits })}</p>
              {!isCurrent && (
                <button onClick={() => upgrade(p.id)} disabled={busy} className="btn-secondary w-full mt-3 text-sm">
                  {t(hasPlan || hasSubscription ? 'billing.switchPlan' : 'billing.subscribe')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <h2 className="font-semibold text-gray-900 dark:text-lc-text mb-3">{t('billing.creditHistoryTitle')}</h2>
      {history.length === 0 ? (
        <p className="text-gray-400 dark:text-lc-faint text-sm">{t('billing.noCreditHistory')}</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {history.map((h) => (
            <div key={h.id} className="p-3 flex items-center justify-between text-sm hover:bg-gray-50/60 dark:hover:bg-lc-surface transition-colors">
              <span className="text-gray-600 dark:text-lc-muted">{h.reason}</span>
              <span className={`font-medium ${h.delta < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {h.delta > 0 ? '+' : ''}{h.delta}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
